import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'

import classNames from 'classnames'
import { MentionEditor, MentionEditorRef } from 'features/mention-editor'
import {
  familyMembersWithAivardSelector, familyMembersWithAvatarsSelector,
} from 'features/team/model/selectors/teamSelectors'
import { useAppSelector } from 'shared/config/store'
import { useOnClickOutside } from 'shared/hooks/useOnclickOutside'
import styled from 'styled-components'

export interface IMessageWriterProps {
  onClickOutside?: () => void
  onSend?: (payload: { message: string, mentions: { user_id: string, name: string }[] }) => void
  defaultValue?: string
  type?: 'new' | 'edit'
  placeholder?: string
  menuPosition?: 'top' | 'bottom'
  additionalBottomContent?: React.ReactNode
  className?: string
  initialFocus?: boolean
  onFocusChange: (focused: boolean) => void
  onAnnotateClick?: () => void
}

export interface MessageWriterRef {
  focus: () => void
  blur: () => void
  isFocused: () => boolean
  hasUnsavedContent: () => boolean
}

export const MessageWriter = forwardRef<MessageWriterRef, IMessageWriterProps>(({
  onClickOutside,
  onSend,
  defaultValue = '',
  type = 'new',
  placeholder = 'Share your thoughts...',
  // menuPosition = 'top',
  additionalBottomContent,
  className,
  initialFocus = false,
  onAnnotateClick,
  onFocusChange
}, ref) => {
  const [content, setContent] = useState(defaultValue || '')
  const [isFocused, setIsFocused] = useState(initialFocus)
  const messageWriterRef = useRef<HTMLDivElement>(null)
  const mentionEditorRef = useRef<MentionEditorRef>(null)
  const users = useAppSelector(familyMembersWithAivardSelector)

  useImperativeHandle(ref, () => ({
    focus: () => {
      mentionEditorRef.current?.focusEnd()
    },
    blur: () => {
      const editor = messageWriterRef.current?.querySelector('[contenteditable]') as HTMLElement
      editor?.blur()
    },
    isFocused: () => isFocused,
    hasUnsavedContent: () => content.trim().length > 0,
  }), [isFocused, content])

  const handleSubmit = (data: { message: string, mentions: { user_id: string, name: string }[] }) => {
    if (data?.message?.trim()) {
      onSend?.(data)
      setContent('')
    }
  }

  useOnClickOutside(messageWriterRef, () => {
    if (onClickOutside) {
      onClickOutside()
    }
  })

  const handleFocusChange = (focused: boolean) => {
    setIsFocused(focused)
    onFocusChange?.(focused)
  }

  return (
    <STextAreaContainer className={classNames(className, { focused: isFocused })}
      ref={messageWriterRef}
      onMouseDown={(e) => {
        if (e.currentTarget === e.target) {
          e.preventDefault()
          const rect = e.currentTarget.getBoundingClientRect()
          const clickX = e.clientX - rect.left
          const isLeftSide = clickX < rect.width / 2
          if (isLeftSide) {
            mentionEditorRef.current?.focusStart()
          } else {
            mentionEditorRef.current?.focusEnd()
          }
        }
      }}
    >
      <MentionEditor
        ref={mentionEditorRef}
        initialValue={defaultValue}
        buttonType={type}
        placeholder={placeholder}
        users={users.map(user => ({
          id: user.id,
          name: user.name,
          phone_or_email: (user as any).phone_or_email || (user as any).email || '',
          avatarUrl: (user as any).avatarUrl,
        }))}
        onSubmit={handleSubmit}
        onCancel={onClickOutside}
        onAnnotateClick={onAnnotateClick}
        showAnnotationButton={false}
        additionalBottomContent={additionalBottomContent}
        initialFocus={initialFocus}
        onFocusChange={handleFocusChange}
      />
    </STextAreaContainer>
  )
})

MessageWriter.displayName = 'MessageWriter'

const STextAreaContainer = styled.div`
  position: relative;
  width: 100%;
  border: 1px solid var(--color-dark-border);
  padding: 12px;
  background-color: var(--color-dark-bg);
  cursor: text;
  position: relative;
  box-sizing: border-box;
  min-height: 92px;
  border-radius: 6px;

  &:focus, &.focused {
    border-color: var(--color-primary);
    box-shadow: 0 0 0 2px rgba(135, 71, 223, 0.10);
  }

  &:focus-within {
    border-color: var(--color-primary);
    box-shadow: 0 0 0 2px rgba(135, 71, 223, 0.10);
  }
`
